<template>
  <Drawer :direction="drawerDirection" :fixed="false" :open="open" :should-scale-background="false" @update:open="handleOpenChange">
    <DrawerContent :class="contentClass" :full-height="fullHeight && drawerDirection === 'bottom'" :position="drawerDirection" @open-auto-focus.prevent>
      <div class="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <!-- タブ付きは見出しとタブ名が二重に並ぶため、現在地はパンくずで示す -->
        <div v-if="hasTabs" class="flex shrink-0 items-center justify-between gap-2.5 border-b border-beige3 px-4 py-[14px]">
          <span class="truncate text-[12px] leading-[18px] tracking-[2.16px] text-gray4">{{ headerLabel }}</span>
          <button aria-label="ドロワーを閉じる" class="drawer-close-button shrink-0 cursor-pointer" type="button" @click="handleClose">close</button>
        </div>
        <template v-else>
          <!-- Mobile: Header with title and close button side by side -->
          <div class="flex shrink-0 items-center justify-between px-3 py-2 md:hidden">
            <h2 v-if="title" class="drawer-mobile-title">{{ title }}</h2>
            <button aria-label="ドロワーを閉じる" class="drawer-close-button cursor-pointer" type="button" @click="handleClose">close</button>
          </div>
          <!-- Desktop: Close button absolute positioned -->
          <button aria-label="ドロワーを閉じる" class="drawer-close-button absolute right-3 top-3 z-10 hidden cursor-pointer md:block" type="button" @click="handleClose">close</button>
        </template>
        <div
          v-if="showTabBar"
          class="drawer-tabs flex shrink-0 gap-5 overflow-x-auto whitespace-nowrap border-b border-gray1 px-4 md:mt-2"
          role="tablist"
          @keydown="handleTabKeydown"
        >
          <button
            v-for="(tab, index) in tabs"
            :id="`${tabIdPrefix}-tab-${index}`"
            :key="index"
            :ref="(el) => setTabRef(el, index)"
            class="drawer-tab shrink-0 cursor-pointer border-b-2 py-3 text-[15px] transition-colors"
            role="tab"
            type="button"
            :aria-controls="tabPanelId"
            :aria-selected="index === activeTabIndex"
            :class="index === activeTabIndex ? 'border-gold1 text-gold1' : 'border-transparent text-gray4'"
            :tabindex="index === activeTabIndex ? 0 : -1"
            @click="handleSelectTab(index)"
          >
            {{ tab.tabName }}
          </button>
        </div>
        <!-- Content area -->
        <div
          :id="showTabBar ? tabPanelId : undefined"
          ref="contentAreaRef"
          class="drawer-content-area min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-2 [-webkit-overflow-scrolling:touch] md:pb-8"
          :aria-labelledby="showTabBar ? `${tabIdPrefix}-tab-${activeTabIndex}` : undefined"
          :class="hasTabs ? 'has-tabs md:pt-4' : 'md:px-8 md:pt-14'"
          :data-vaul-no-drag="allowsTextSelection ? '' : undefined"
          :role="showTabBar ? 'tabpanel' : undefined"
          :tabindex="showTabBar ? 0 : undefined"
        >
          <!-- Desktop: Title inside content -->
          <h2 v-if="title && !hasTabs" class="hidden text-xl font-medium leading-7 tracking-[0.5px] text-gray9 md:block">{{ title }}</h2>
          <div class="drawer-rich-text-wrapper min-w-0 break-words py-2" :class="hasTabs ? 'px-0' : 'px-1'">
            <StyledRichText v-if="content" :html="content" />
          </div>
        </div>
        <!-- 読み終えたゲストが元の導線へ戻れるよう、本文のスクロールと独立して置く -->
        <div v-if="cta" class="shrink-0 border-t border-gray1 px-4 py-3 md:py-4">
          <PrimaryButton :on-click="cta.onClick">{{ cta.label }}</PrimaryButton>
        </div>
      </div>
    </DrawerContent>
  </Drawer>
</template>

<script setup lang="ts">
import { useEventListener, useMediaQuery } from '@vueuse/core';
import { useId } from 'vue';

import PrimaryButton from '@/components/atoms/PrimaryButton/PrimaryButton.vue';
import { Drawer, DrawerContent } from '@/components/lib/shadcn-vue/drawer';
import { StyledRichText } from '@/wabunka-src/common/features/Featured/components/StyledRichText';

interface DrawerTabItem {
  tabKey: string;
  tabName: string;
}

interface Props {
  open: boolean;
  title: string;
  content?: string;
  /** 渡さなければ従来どおりタブ無しのドロワーになる（ADR-145 / D-011） */
  tabs?: DrawerTabItem[];
  selectedTabIndex?: number;
  /** 本文の下に固定で出す導線。渡さなければ出ない（D-020） */
  cta?: { label: string; onClick: () => void };
  /** SP で画面いっぱいに開く（D-020） */
  fullHeight?: boolean;
  onSelectTab?: (index: number) => void;
  onOpenChange?: (open: boolean) => void;
}
const props = defineProps<Props>();
const handleOpenChange = (open: boolean) => {
  props.onOpenChange?.(open);
};
const handleClose = () => {
  handleOpenChange(false);
};

const hasTabs = computed(() => (props.tabs?.length ?? 0) > 0);
// 省略された回でも必ずどれか1つが選択されている状態にする（全タブが tabindex=-1 だとキーボードで入れない）
const activeTabIndex = computed(() => props.selectedTabIndex ?? 0);
// タブが1件のときはタブバーを出さず本文だけを見せる（H-001 の暫定案）
const showTabBar = computed(() => (props.tabs?.length ?? 0) > 1);
const selectedTabName = computed(() => props.tabs?.[activeTabIndex.value]?.tabName ?? '');
// 見出しが未入稿でも空にしない（ADR-145 / D-021）
const headerLabel = computed(() => props.title || selectedTabName.value);
// 同一ページに複数のドロワーが載るため、タブと本文を結ぶ id はインスタンスごとに分ける
const tabIdPrefix = useId();
const tabPanelId = `${tabIdPrefix}-panel`;
const contentAreaRef = ref<HTMLElement | null>(null);
const tabRefs = ref<HTMLButtonElement[]>([]);
const setTabRef = (el: unknown, index: number) => {
  if (el instanceof HTMLButtonElement) tabRefs.value[index] = el;
};
const handleSelectTab = (index: number) => {
  props.onSelectTab?.(index);
};

// WAI-ARIA の tabs パターン。選択中のタブだけが Tab 順に入り、タブ間は矢印キーで移る
const TAB_KEY_MOVES: Record<string, (current: number, count: number) => number> = {
  ArrowRight: (current, count) => (current + 1) % count,
  ArrowLeft: (current, count) => (current - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
};
const handleTabKeydown = (event: KeyboardEvent) => {
  const move = TAB_KEY_MOVES[event.key];
  const count = props.tabs?.length ?? 0;
  if (!move || count === 0) return;
  event.preventDefault();
  const next = move(activeTabIndex.value, count);
  handleSelectTab(next);
  nextTick(() => tabRefs.value[next]?.focus());
};

// タブバーは横スクロールするので、後方のタブを指す起動リンクで開いたときに選択中タブが見えない位置に残らないようにする
const revealSelectedTab = () => {
  const index = activeTabIndex.value;
  nextTick(() => tabRefs.value[index]?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' }));
};
watch(
  () => props.selectedTabIndex,
  () => {
    if (contentAreaRef.value) contentAreaRef.value.scrollTop = 0;
    revealSelectedTab();
  },
);
watch(
  () => props.open,
  (open) => {
    if (open) revealSelectedTab();
  },
);

const isDesktop = useMediaQuery('(min-width: 768px)');
const drawerDirection = computed<'bottom' | 'right'>(() => (isDesktop.value ? 'right' : 'bottom'));
// vaul はドラッグ中かどうかを先に決めるため、切らないと選択操作がドラッグに吸われる。テキスト選択を許している条件（DrawerContent.vue）に合わせる
const allowsTextSelection = useMediaQuery('(hover: hover) and (pointer: fine)');

// 開いている間の全選択はドロワーの外まで掴んでも使い道が無いため、本文だけを選び直す
const selectContentArea = (event: KeyboardEvent) => {
  if (!props.open || !allowsTextSelection.value) return;
  if (event.key !== 'a' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
  const contentArea = contentAreaRef.value;
  if (!contentArea) return;
  event.preventDefault();
  const range = document.createRange();
  range.selectNodeContents(contentArea);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
};
useEventListener(document, 'keydown', selectContentArea);

const contentClass = computed(() => {
  const base = 'titled-rich-text-drawer-panel bg-white font-Spectral';
  if (isDesktop.value) return `${base} h-full w-[600px]`;
  return props.fullHeight ? base : `${base} max-h-[85dvh] [&_.bg-muted]:hidden`;
});
</script>

<style scoped>
.drawer-mobile-title {
  color: var(--foreground-fg-primary, #1a150e);
  font-size: var(--font-size-p1, 16px);
  font-style: normal;
  font-weight: 700;
  line-height: var(--line-height-p1, 24px);
}

.drawer-close-button {
  color: var(--foreground-fg-accent, #826b3d);
  text-align: center;
  font-family: 'Material Symbols Sharp';
  font-size: var(--font-size-IconFont, 24px);
  font-style: normal;
  font-weight: 100;
  line-height: var(--line-height-IconFont, 24px);
}

.drawer-rich-text-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 767px) {
  .drawer-rich-text-wrapper {
    color: var(--foreground-fg-muted, #423d36);
    font-size: var(--font-size-p2, 14px);
    font-style: normal;
    font-weight: 400;
    line-height: var(--line-height-p2, 24px);
  }
}

.drawer-content-area :deep(table) {
  table-layout: fixed;
  width: 100%;
  max-width: 100%;
}

.drawer-content-area :deep(table th),
.drawer-content-area :deep(table td) {
  min-width: 0 !important;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* タブ付き以外は従来どおり幅を上書きする（タブ付きは共通のリッチテキストが見出し列に持つ幅（PC 160px）に任せる。両方に width を当てると table-fixed が半々に割る） */
.drawer-content-area:not(.has-tabs) :deep(table th),
.drawer-content-area:not(.has-tabs) :deep(table td) {
  width: 1% !important;
}

.drawer-content-area.has-tabs :deep(table th),
.drawer-content-area.has-tabs :deep(table td) {
  padding-block: 8px;
}

.drawer-content-area :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>

<style>
.titled-rich-text-drawer-panel:focus {
  outline: none;
}

.titled-rich-text-drawer-panel {
  user-select: text;
}
</style>
